/**
 * Schedule event categories shared between artist-side and org-side dialogs.
 *
 * The backend accepts any non-empty string for `ScheduleEvent.category`
 * (test_schedule_event_category_is_free_string pins this contract). The
 * dropdown below covers the daily-use cases observed during festival
 * production; "other" + "Custom…" let the org/artist type a one-off.
 */

import { useState } from "react";

export const CATEGORY_OPTIONS: { value: string; label: string }[] = [
  { value: "soundcheck", label: "Soundcheck" },
  { value: "rehearsal", label: "Rehearsal" },
  { value: "set", label: "Set / Performance" },
  { value: "press", label: "Press call" },
  { value: "interview", label: "Interview" },
  { value: "photo_session", label: "Photo session" },
  { value: "meet_greet", label: "Meet & greet" },
  { value: "signing", label: "Autograph signing" },
  { value: "vip", label: "VIP / Hospitality" },
  { value: "briefing", label: "Production briefing" },
  { value: "transfer", label: "Transfer / Pickup" },
  { value: "dinner", label: "Dinner / Catering" },
  { value: "day_off", label: "Day off" },
  { value: "other", label: "Other" },
];

const SELECT_CLASS =
  "input";

const INPUT_CLASS =
  "input";

const CUSTOM_SENTINEL = "__custom__";

/**
 * Dropdown of common schedule-event categories with a "Custom…" escape hatch
 * for one-off labels. Wraps `<select>` + an optional inline `<input>` that
 * appears when the user picks "Custom…".
 *
 * Free-typing is preserved: the parent only ever sees the resolved string
 * value (never the sentinel).
 */
export function CategorySelect({
  value,
  onChange,
}: {
  value: string;
  onChange: (next: string) => void;
}) {
  const isKnown = CATEGORY_OPTIONS.some((o) => o.value === value);
  const [showCustom, setShowCustom] = useState(!isKnown && value !== "");

  const selectValue = showCustom || !isKnown ? CUSTOM_SENTINEL : value;

  return (
    <div className="space-y-2">
      <select
        className={SELECT_CLASS}
        value={selectValue}
        onChange={(e) => {
          const next = e.target.value;
          if (next === CUSTOM_SENTINEL) {
            setShowCustom(true);
            onChange(""); // Clear so the user types their own
          } else {
            setShowCustom(false);
            onChange(next);
          }
        }}
      >
        {CATEGORY_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
        <option value={CUSTOM_SENTINEL}>Custom…</option>
      </select>
      {showCustom && (
        <input
          className={INPUT_CLASS}
          placeholder="Type your own category (e.g. brand_activation)"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoFocus
        />
      )}
    </div>
  );
}
